import './styles.css'

import { sdk, miniAppHost } from '@farcaster/miniapp-sdk'
import { recoverMessageAddress } from 'viem'

const SAMPLE_LOOKUP_USER_ID = '0xfb8a4DD9F4614eEeA4812dAf3EBe906b97b1d841'

const appRoot = document.querySelector<HTMLDivElement>('#app')

if (!appRoot) {
  throw new Error('Failed to initialise app container')
}

appRoot.innerHTML = `
  <main class="layout">
    <header class="layout__header">
      <h1 class="layout__title">Create Poll</h1>
    </header>
    <section class="layout__content">
      <div class="debug">
        <h2 class="debug__title">Debug</h2>
        <div class="debug__group">
          <h3 class="debug__subtitle">Mini App SDK</h3>
          <div class="btns">
            <button id="btn-context" class="btn">context</button>
            <button id="btn-getCapabilities" class="btn">getCapabilities</button>
            <button id="btn-getChains" class="btn">getChains</button>
            <button id="btn-isInMiniApp" class="btn">isInMiniApp</button>
            <button id="btn-logSdk" class="btn">log sdk</button>
            <button id="btn-logMiniAppHost" class="btn">log miniAppHost</button>
          </div>
        </div>
        <div class="debug__group">
          <h3 class="debug__subtitle">Actions</h3>
          <div class="btns">
            <button id="btn-addMiniApp" class="btn">addMiniApp</button>
            <button id="btn-close" class="btn">close</button>
            <button id="btn-composeCast" class="btn">composeCast</button>
            <button id="btn-logHello" class="btn">Log Hello</button>
            <button id="btn-signin" class="btn">signin</button>
            <button id="btn-openUrl" class="btn">openUrl</button>
            <button id="btn-viewProfile" class="btn">viewProfile</button>
            <button id="btn-viewCast" class="btn">viewCast</button>
            <button id="btn-swapToken" class="btn">swapToken</button>
            <button id="btn-sendToken" class="btn">sendToken</button>
            <button id="btn-viewToken" class="btn">viewToken</button>
          </div>
        </div>
        <div class="debug__group">
          <h3 class="debug__subtitle">Wallet actions</h3>
          <button id="btn-getEthProvider" class="btn">getEthereumProvider</button>
          <div class="btns btns--wallet">
            <button id="btn-ethChainId" class="btn">eth_chainId</button>
            <button id="btn-ethBlockNumber" class="btn">eth_blockNumber</button>
            <button id="btn-ethSignData" class="btn">personal_sign demo</button>
            <button id="btn-signTypedData" class="btn">sign typed data</button>
          </div>
        </div>
        <div class="debug__group">
          <h3 class="debug__subtitle">Transactions</h3>
          <div class="btns btns--wallet">
            <button id="btn-sendSelfTx" class="btn">send self tx</button>
            <button id="btn-ethSignTx" class="btn">eth_signTransaction</button>
            <button id="btn-ethSendRawTx" class="btn">eth_sendRawTransaction</button>
          </div>
          <div class="debug__walletLinked">
            <p class="debug__walletHeading">Linked wallets</p>
            <button id="btn-wallet-getNonces" class="btn btn--full">Get Nonces</button>
            <p id="wallet-linked-wallets-status" class="debug__walletStatus" aria-live="polite"></p>
          </div>
        </div>
        <div class="debug__group debug__group--output">
          <h3 class="debug__subtitle">Output</h3>
          <div id="debug-output" class="debug__output" role="log" aria-live="polite" aria-label="Debug output"></div>
        </div>
      </div>
    </section>
  </main>
  <noscript class="layout__noscript">JavaScript is required to use the poll mini app.</noscript>
`

const outputContainer = document.querySelector<HTMLDivElement>('#debug-output')
const linkedWalletGetNoncesButton = document.querySelector<HTMLButtonElement>('#btn-wallet-getNonces')
const linkedWalletStatus = document.querySelector<HTMLParagraphElement>('#wallet-linked-wallets-status')
const OUTPUT_MAX_ENTRIES = 20
const MINIAPP_CONTEXT_TIMEOUT_MS = 10000
const MINIAPP_CONTEXT_TIMEOUT_ERROR = 'miniapp-context-timeout'

// Track SDK context loading time using isInMiniApp for reliable detection
const sdkContextLoadStartTime = performance.now()
console.log('[SDK] Checking if running in miniapp host...')

// Use isInMiniApp() to wait for host readiness, then get context
const miniAppContextPromise = (async () => {
  // isInMiniApp() internally waits for context with a timeout
  // Cast to access the timeoutMs parameter (not exposed in v0.2.1 types but exists in implementation)
  const isInMiniApp = await (sdk.isInMiniApp as (timeoutMs?: number) => Promise<boolean>)(MINIAPP_CONTEXT_TIMEOUT_MS)
  const checkTime = performance.now() - sdkContextLoadStartTime

  if (!isInMiniApp) {
    console.warn(`[SDK] Not running in miniapp host (checked in ${checkTime.toFixed(2)}ms)`)
    throw new Error(MINIAPP_CONTEXT_TIMEOUT_ERROR)
  }

  console.log(`[SDK] Confirmed running in miniapp host (${checkTime.toFixed(2)}ms), fetching context...`)
  const context = await sdk.context
  const totalTime = performance.now() - sdkContextLoadStartTime
  console.log(`[SDK] Context fully loaded in ${totalTime.toFixed(2)}ms`, context)

  await sdk.actions.ready()
  logDebug('[debug] sdk.actions.ready() called')

  return context
})()

function formatOutputPayload(value: unknown): string {
  if (value instanceof Error) {
    return value.stack ?? `${value.name}: ${value.message}`
  }

  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' || typeof value === 'boolean' || value === null || value === undefined) {
    return String(value)
  }

  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function writeOutput(label: string, payload?: unknown) {
  if (!outputContainer) return

  const entry = document.createElement('div')
  entry.className = 'debug__outputEntry'

  const timestamp = new Date().toLocaleTimeString()
  const formattedPayload = payload === undefined ? '' : formatOutputPayload(payload)
  entry.textContent = formattedPayload ? `[${timestamp}] ${label} ${formattedPayload}` : `[${timestamp}] ${label}`

  outputContainer.prepend(entry)

  while (outputContainer.childElementCount > OUTPUT_MAX_ENTRIES) {
    const last = outputContainer.lastElementChild
    if (!last) break
    outputContainer.removeChild(last)
  }
}

function logDebug(label: string, payload?: unknown) {
  if (payload === undefined) {
    console.log(label)
  } else {
    console.log(label, payload)
  }

  writeOutput(label, payload)
}

function utf8ToHex(value: string) {
  const encoder = new TextEncoder()
  const bytes = encoder.encode(value)
  return `0x${Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')}`
}

async function recoverSignerFromSignature(message: string, signature: string) {
  try {
    return await recoverMessageAddress({ message, signature })
  } catch (error) {
    logDebug('[debug] personal_sign recovery error:', error)
    return null
  }
}

type LinkedWallet = {
  address?: string | null
}

type TownsUserContext = Awaited<typeof sdk.context>['user'] & {
  towns?: {
    linkedWallets?: LinkedWallet[]
  }
}

type HostUserRecord = {
  userId: string
  username?: string
  displayName?: string
  pfpUrl?: string
}

type MiniAppHostWithUserLookup = typeof miniAppHost & {
  getUserById?: (userId: string) => HostUserRecord | null | Promise<HostUserRecord | null>
}

const hostWithUserLookup = miniAppHost as MiniAppHostWithUserLookup

function setLinkedWalletStatus(message: string) {
  if (!linkedWalletStatus) return
  linkedWalletStatus.textContent = message
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(timeoutMessage))
    }, timeoutMs)

    promise
      .then((value) => {
        window.clearTimeout(timeoutId)
        resolve(value)
      })
      .catch((error) => {
        window.clearTimeout(timeoutId)
        reject(error)
      })
  })
}

function getMiniAppContextWithTimeout() {
  return withTimeout(miniAppContextPromise, MINIAPP_CONTEXT_TIMEOUT_MS, MINIAPP_CONTEXT_TIMEOUT_ERROR)
}

type ActionHandler = () => Promise<void>

function onClick(id: string, handler: ActionHandler) {
  const el = document.getElementById(id)
  if (!el) return

  el.addEventListener('click', () => {
    handler().catch((err) => {
      console.error(`[debug:${id}] error`, err)
      writeOutput(`[debug:${id}] error`, err)
    })
  })
}

onClick('btn-context', async () => {
  const context = await sdk.context
  logDebug('[debug] context:', context)
})

onClick('btn-getCapabilities', async () => {
  const capabilities = await sdk.getCapabilities()
  logDebug('[debug] getCapabilities result:', capabilities)
})

onClick('btn-getChains', async () => {
  const chains = await sdk.getChains()
  logDebug('[debug] getChains result:', chains)
})

onClick('btn-isInMiniApp', async () => {
  const isInMiniApp = await sdk.isInMiniApp()
  logDebug('[debug] isInMiniApp result:', isInMiniApp)
})

onClick('btn-logSdk', async () => {
  logDebug('[debug] sdk object:', sdk)
})

onClick('btn-logMiniAppHost', async () => {
  console.log('[debug] miniAppHost proxy:', miniAppHost)
  logDebug('[debug] miniAppHost proxy logged to console. Checking getUserById support…')

  const lookupFn = hostWithUserLookup.getUserById
  if (typeof lookupFn !== 'function') {
    logDebug('[debug] miniAppHost.getUserById unavailable on this host')
    return
  }

  try {
    const lookupResult = await lookupFn(SAMPLE_LOOKUP_USER_ID)
    logDebug(
      `[debug] miniAppHost.getUserById(${SAMPLE_LOOKUP_USER_ID}):`,
      lookupResult ?? 'No user returned'
    )
  } catch (error) {
    logDebug('[debug] miniAppHost.getUserById error:', error)
  }
})

onClick('btn-addMiniApp', async () => {
  const result = await sdk.actions.addMiniApp()
  logDebug('[debug] addMiniApp result:', result)
})

onClick('btn-close', async () => {
  await sdk.actions.close()
  logDebug('[debug] close invoked')
})

onClick('btn-composeCast', async () => {
  const result = await sdk.actions.composeCast({ text: 'Hello from Poll App', embeds: ['https://pollapp-miniapp-5sb.pages.dev/?hello=true'] })
  logDebug('[debug] composeCast result:', result)
})

onClick('btn-logHello', async () => {
  logDebug('[debug] Hello from Miniapp')
})

onClick('btn-signin', async () => {
  const result = await sdk.actions.signIn({ nonce: '1234567890' })
  logDebug('[debug] signIn result:', result)
})

onClick('btn-openUrl', async () => {
  const url = 'https://towns.com'
  await sdk.actions.openUrl(url)
  logDebug('[debug] openUrl invoked with', url)
})

onClick('btn-viewProfile', async () => {
  const fid = 1
  await sdk.actions.viewProfile({ fid })
  logDebug('[debug] viewProfile invoked for fid', fid)
})

onClick('btn-viewCast', async () => {
  const hash = '0x0123456789abcdef0123456789abcdef0123456789abcdef0123456789abcdef'
  await sdk.actions.viewCast({ hash })
  logDebug('[debug] viewCast invoked for hash', hash)
})

onClick('btn-swapToken', async () => {
  const result = await sdk.actions.swapToken({
    sellToken: 'eip155:8453/erc20:0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    buyToken: 'eip155:10/native',
    sellAmount: '100000'
  })
  logDebug('[debug] swapToken result:', result)
})

onClick('btn-sendToken', async () => {
  const result = await sdk.actions.sendToken({
    token: 'eip155:84532/erc20:0x036cbd53842c5426634e7929541ec2318f3dcf7e',
    amount: '1',
    recipientFid: 1,
    recipientAddress: '0xFA9eEc9FBA16303eaE51EB0ef3F7e090035e3e1A',
  })
  logDebug('[debug] sendToken result:', result)
})

onClick('btn-viewToken', async () => {
  await sdk.actions.viewToken({
    token: 'eip155:8453/erc20:0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  })
  logDebug('[debug] viewToken invoked')
})

onClick('btn-getEthProvider', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  logDebug('[debug] getEthereumProvider:', provider)
})

onClick('btn-ethChainId', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  if (!provider?.request) {
    logDebug('[debug] eth_chainId unavailable: provider missing request method')
    return
  }

  const chainId = await provider.request({ method: 'eth_chainId' })
  logDebug('[debug] eth_chainId result:', chainId)
})

onClick('btn-ethBlockNumber', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  if (!provider?.request) {
    logDebug('[debug] eth_blockNumber unavailable: provider missing request method')
    return
  }

  const blockNumber = await provider.request({ method: 'eth_blockNumber' })
  logDebug('[debug] eth_blockNumber result:', blockNumber)
})

onClick('btn-ethSignData', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  if (!provider?.request) {
    logDebug('[debug] personal_sign unavailable: provider missing request method')
    return
  }

  try {
    const accounts = await provider.request({ method: 'eth_accounts' })
    if (!Array.isArray(accounts) || accounts.length === 0) {
      logDebug('[debug] personal_sign aborted: eth_accounts returned no addresses')
      return
    }

    const context = await getMiniAppContextWithTimeout()
    const linkedWallets = context.towns?.user?.linkedWallets ?? []
    const signer = linkedWallets[0]?.address as `0x${string}`
    const message = `Miniapp signature demo @ ${new Date().toISOString()}`
    const hexPayload = utf8ToHex(message) as `0x${string}`

    const signature = await provider.request({ method: 'personal_sign', params: [hexPayload, signer] })
    const actualSigner = await recoverSignerFromSignature(message, signature)
    logDebug(`[debug] personal_sign result for ${actualSigner}:`, signature)
  } catch (error) {
    logDebug('[debug] personal_sign error:', error)
  }
})

async function buildSelfTxParams() {
  const context = await getMiniAppContextWithTimeout()
  const linkedWallets = context.towns?.user?.linkedWallets ?? []
  const fromAddress = linkedWallets[0]?.address

  if (!fromAddress) {
    throw new Error('No linked wallets available in context')
  }

  return {
    from: fromAddress,
    to: fromAddress,
    value: '0x1'
  }
}

onClick('btn-sendSelfTx', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  if (!provider?.request) {
    logDebug('[debug] eth_sendTransaction unavailable: provider missing request method')
    return
  }

  try {
    const txParams = await buildSelfTxParams()

    const txHash = await provider.request({ method: 'eth_sendTransaction', params: [txParams] })
    logDebug('[debug] eth_sendTransaction hash:', txHash)
  } catch (error) {
    logDebug('[debug] eth_sendTransaction error:', error)
  }
})

onClick('btn-signTypedData', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  if (!provider?.request) {
    logDebug('[debug] eth_signTypedData_v4 unavailable: provider missing request method')
    return
  }

  try {
    const context = await getMiniAppContextWithTimeout()
    const linkedWallets = context.towns?.user?.linkedWallets ?? []
    const signer = linkedWallets[0]?.address

    if (!signer) {
      logDebug('[debug] eth_signTypedData_v4 aborted: no linked wallets in context')
      return
    }

    const typedData = {
      domain: {
        name: 'Miniapp Demo',
        version: '1',
        chainId: Number(context.towns?.chainId ?? 1),
        verifyingContract: signer
      },
      primaryType: 'DemoSign',
      message: {
        contents: 'Miniapp typed data signature',
        timestamp: new Date().toISOString(),
        wallet: signer
      },
      types: {
        EIP712Domain: [
          { name: 'name', type: 'string' },
          { name: 'version', type: 'string' },
          { name: 'chainId', type: 'uint256' },
          { name: 'verifyingContract', type: 'address' }
        ],
        DemoSign: [
          { name: 'contents', type: 'string' },
          { name: 'timestamp', type: 'string' },
          { name: 'wallet', type: 'address' }
        ]
      }
    }

    const payload = JSON.stringify(typedData)
    const signature = await provider.request({ method: 'eth_signTypedData_v4', params: [signer, payload] })
    logDebug('[debug] eth_signTypedData_v4 signature:', signature)
  } catch (error) {
    logDebug('[debug] eth_signTypedData_v4 error:', error)
  }
})

async function requestEthGetTransactionCount(address: string): Promise<boolean> {
  const normalizedAddress = address.trim() as `0x${string}`

  try {
    const provider = await sdk.wallet.getEthereumProvider()
    if (!provider?.request) {
      logDebug('[debug] eth_getTransactionCount unavailable: provider missing request method')
      return false
    }

    try {
      const result = await provider.request({ method: 'eth_getTransactionCount', params: [normalizedAddress, 'latest'] })
      logDebug(`[debug] eth_getTransactionCount result for ${normalizedAddress}:`, result)
      return true
    } catch (error) {
      logDebug(`[debug] eth_getTransactionCount error for ${normalizedAddress}:`, error)
      return false
    }
  } catch (error) {
    logDebug(`[debug] eth_getTransactionCount unexpected error for ${normalizedAddress}:`, error)
    return false
  }
}

async function handleLinkedWalletGetNoncesClick() {
  if (linkedWalletGetNoncesButton) {
    linkedWalletGetNoncesButton.disabled = true
  }

  setLinkedWalletStatus('Loading linked wallets…')

  try {
    const context = await getMiniAppContextWithTimeout()
    const linkedWallets = context.towns?.user?.linkedWallets ?? []
    const walletAddresses = linkedWallets
      .map((wallet) => wallet?.address?.trim())
      .filter((address): address is string => Boolean(address))

    if (!walletAddresses.length) {
      setLinkedWalletStatus('No linked wallets detected in context.')
      return
    }

    setLinkedWalletStatus('Requesting eth_getTransactionCount for linked wallets…')

    let successCount = 0
    for (const address of walletAddresses) {
      const isSuccess = await requestEthGetTransactionCount(address)
      if (isSuccess) {
        successCount += 1
      }
    }

    const total = walletAddresses.length
    setLinkedWalletStatus(
      `eth_getTransactionCount dispatched for ${total} linked wallet${total === 1 ? '' : 's'} (${successCount} successful). Check debug output for payloads.`
    )
  } catch (error) {
    if (error instanceof Error && error.message === MINIAPP_CONTEXT_TIMEOUT_ERROR) {
      setLinkedWalletStatus('Timed out waiting for mini app context. Open this app inside a Farcaster client to access linked wallets.')
    } else {
      setLinkedWalletStatus('Unable to load linked wallets from context.')
      logDebug('[debug] linked wallets context read failed:', error)
    }
  } finally {
    if (linkedWalletGetNoncesButton) {
      linkedWalletGetNoncesButton.disabled = false
    }
  }
}

if (linkedWalletGetNoncesButton) {
  linkedWalletGetNoncesButton.addEventListener('click', () => {
    handleLinkedWalletGetNoncesClick().catch((error) => {
      logDebug('[debug] eth_getTransactionCount batch error:', error)
    })
  })
}
onClick('btn-ethSignTx', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  if (!provider?.request) {
    logDebug('[debug] eth_signTransaction unavailable: provider missing request method')
    return
  }

  try {
    const txParams = await buildSelfTxParams()
    const signature = await provider.request({ method: 'eth_signTransaction', params: [txParams] })
    logDebug('[debug] eth_signTransaction result:', signature)
  } catch (error) {
    logDebug('[debug] eth_signTransaction error:', error)
  }
})

onClick('btn-ethSendRawTx', async () => {
  const provider = await sdk.wallet.getEthereumProvider()
  if (!provider?.request) {
    logDebug('[debug] eth_sendRawTransaction unavailable: provider missing request method')
    return
  }

  try {
    const context = await getMiniAppContextWithTimeout()
    const linkedWallets = context.towns?.user?.linkedWallets ?? []
    const signer = linkedWallets[0]?.address

    const txParams = await buildSelfTxParams()
    const signed = await provider.request({ method: 'eth_signTransaction', params: [txParams] })
    logDebug('[debug] eth_signTransaction raw result:', signed)

    if (!signed || typeof signed !== 'string') {
      logDebug('[debug] eth_sendRawTransaction aborted: unexpected signature payload')
      return
    }

    const txHash = await provider.request({ method: 'eth_sendRawTransaction', params: [signed] })
    logDebug('[debug] eth_sendRawTransaction hash:', txHash)
  } catch (error) {
    logDebug('[debug] eth_sendRawTransaction error:', error)
  }
})
